"use client";

import { motion, type Variants } from "framer-motion";
import { type ReactNode } from "react";

import { cn } from "@/lib/utils";

const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

type SectionProps = {
  id?: string;
  eyebrow?: string;
  title?: string;
  description?: string;
  align?: "left" | "center";
  delay?: number;
  className?: string;
  tone?: "light" | "dark";
  children: ReactNode;
};

export function Section({
  id,
  eyebrow,
  title,
  description,
  align = "left",
  delay = 0,
  className,
  tone = "light",
  children,
}: SectionProps) {
  const textAlign = align === "center" ? "text-center mx-auto" : "text-left";
  const headingColor = tone === "dark" ? "text-white" : "text-foreground";
  const descriptionColor =
    tone === "dark" ? "text-white/70" : "text-foreground/70";
  const eyebrowColor =
    tone === "dark" ? "text-accent" : "text-primary font-semibold";

  return (
    <section id={id} className={cn("py-16 sm:py-24", className)}>
      <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
        {(eyebrow || title || description) && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={sectionVariants}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              delay,
            }}
            className={cn("max-w-3xl space-y-4", textAlign)}
          >
            {eyebrow && (
              <p
                className={cn(
                  "text-sm font-semibold uppercase tracking-[0.32em]",
                  eyebrowColor,
                )}
              >
                {eyebrow}
              </p>
            )}
            {title && (
              <h2
                className={cn(
                  "text-3xl font-semibold leading-tight sm:text-4xl",
                  headingColor,
                )}
              >
                {title}
              </h2>
            )}
            {description && (
              <p
                className={cn("text-base sm:text-lg", descriptionColor)}
              >
                {description}
              </p>
            )}
          </motion.div>
        )}
        <motion.div
          initial="hidden"
          whileInView="visible"
          // Use a small amount so very tall sections still animate on mobile viewports
          viewport={{ once: true, amount: 0.05 }}
          variants={sectionVariants}
          transition={{
            duration: 0.5,
            ease: "easeOut",
            delay: delay + 0.1,
          }}
          className="mt-10 sm:mt-12"
        >
          {children}
        </motion.div>
      </div>
    </section>
  );
}
